import { useEffect, useRef } from "react";
import {
  ColorType,
  createChart,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";
import type { BacktestResult, Candle } from "../types";

interface TradeMarker {
  time: UTCTimestamp;
  position: "aboveBar" | "belowBar";
  color: string;
  shape: "arrowDown" | "arrowUp";
  text: string;
}

interface ChartProps {
  candles: Candle[];
  result: BacktestResult | null;
}

export default function Chart({ candles, result }: ChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);

  // ── create chart once
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: "#131722" },
        textColor: "#787B86",
        fontSize: 12,
      },
      grid: {
        vertLines: { color: "#1E222D" },
        horzLines: { color: "#1E222D" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#787B86", width: 1, style: 3, labelBackgroundColor: "#2A2E39" },
        horzLine: { color: "#787B86", width: 1, style: 3, labelBackgroundColor: "#2A2E39" },
      },
      rightPriceScale: {
        borderColor: "#2A2E39",
        scaleMargins: { top: 0.1, bottom: 0.15 },
      },
      timeScale: {
        borderColor: "#2A2E39",
        timeVisible: true,
        secondsVisible: false,
      },
      watermark: {
        visible: true,
        text: "XAUUSD · GoldScope Jev AI",
        color: "rgba(41, 98, 255, 0.08)",
        fontSize: 56,
        horzAlign: "center",
        vertAlign: "center",
      },
      handleScroll: true,
      handleScale: true,
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#26A69A",
      downColor: "#EF5350",
      borderVisible: false,
      wickUpColor: "#26A69A",
      wickDownColor: "#EF5350",
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });

    chartRef.current = chart;
    seriesRef.current = candleSeries;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      chart.applyOptions({ width, height });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // ── feed candle data
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || candles.length === 0) return;

    series.setData(
      candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }))
    );
    chartRef.current?.timeScale().scrollToRealTime();
  }, [candles]);

  // ── draw trade markers after backtest
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    if (!result || result.trades.length === 0) {
      series.setMarkers([]);
      return;
    }

    const firstBar = candles[0]?.time ?? 0;
    const markers: TradeMarker[] = [];

    for (const trade of result.trades) {
      if (trade.entryTime < firstBar) continue;
      markers.push({
        time: trade.entryTime as UTCTimestamp,
        position: trade.action === "BUY" ? "belowBar" : "aboveBar",
        color: trade.action === "BUY" ? "#26A69A" : "#EF5350",
        shape: trade.action === "BUY" ? "arrowUp" : "arrowDown",
        text: `${trade.action} ${trade.lots}`,
      });
    }

    markers.sort((a, b) => a.time - b.time);
    series.setMarkers(markers);
  }, [result, candles]);

  return <div ref={containerRef} className="h-full w-full" />;
}
